import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import chaseBarTree from '../../assets/chase-bar/chase-bar-tree.png';
import { analytics } from '../../services/analytics';
import { useFlag } from '../../services/launchDarkly';
import { EnumOpenDialog } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';

import styles from './ChaseBar.module.scss';
import { TARGET_VALUE, useChaseBarConfig } from './config';
import { useChaseProgress } from './useChaseProgress';

function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatDisplayValue(value: number): string {
  const k = Math.floor(value / 1000);
  return k > 0 ? `${k}K` : '';
}

const ENTRANCE_DURATION_MS = 3500;

// Persists across remounts within the same session — so the entrance animation
// only plays the first time the lobby is reached, not when returning from a game.
let entrancePlayed = false;

const ChaseBar = observer(() => {
  const { t } = useTranslation();
  const { uiStore } = useStores();
  const isEnabled = useFlag('chase_bar_enabled', false);
  const config = useChaseBarConfig();
  const { status, progress, currentValue, timeRemaining } = useChaseProgress(config);

  const [entranceDone, setEntranceDone] = useState(entrancePlayed);
  const [anim, setAnim] = useState({ progress: 0, value: 0 });
  const impressionSentRef = useRef(false);

  const isVisible = isEnabled && status !== 'hidden' && status !== 'ended';

  useEffect(() => {
    if (!isVisible || impressionSentRef.current) return;
    impressionSentRef.current = true;
    void analytics.sendEvent('donation_lobby', {
      event_category: 'donation_bar',
      event_action: 'impression',
    });
  }, [isVisible]);

  useEffect(() => {
    if (entranceDone) return;
    if (status !== 'active' && status !== 'completed') return;

    const targetProgress = progress;
    const targetValue = currentValue;
    const startTime = Date.now();
    let rafId = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(1, elapsed / ENTRANCE_DURATION_MS);
      const eased = ratio < 0.5 ? 4 * ratio * ratio * ratio : 1 - (-2 * ratio + 2) ** 3 / 2;

      setAnim({
        progress: targetProgress * eased,
        value: Math.round(targetValue * eased),
      });

      if (ratio < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        entrancePlayed = true;
        setEntranceDone(true);
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entranceDone, status]);

  if (!isVisible) {
    return null;
  }

  const isCompleted = status === 'completed';
  const shownProgress = entranceDone ? progress : anim.progress;
  const shownValue = entranceDone ? currentValue : anim.value;
  const isFullyFilled = isCompleted && entranceDone;
  const fillPercent = `${(shownProgress * 100).toFixed(1)}%`;
  const countdown = formatTimeRemaining(timeRemaining);

  const handleClick = () => {
    void analytics.sendEvent('donation_info', {
      event_category: 'donation_bar',
      event_action: 'click',
    });
    uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenChaseBarInfoDialog });
  };

  return (
    <div className={classNames(styles.chaseBar, { [styles.completed]: isFullyFilled })}>
      {isFullyFilled && (
        <div className={styles.sparkles}>
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
          <span className={styles.spark} />
        </div>
      )}
      <div className={styles.barContainer}>
        {!isCompleted && <div className={styles.timer}>{countdown}</div>}

        <div className={styles.trackOuter} onClick={handleClick}>
          <div className={styles.track}>
            <div className={styles.fill} style={{ width: fillPercent }}>
              {isFullyFilled ? (
                <span className={styles.completedText}>{t('chaseBar.completed')}</span>
              ) : (
                <span className={styles.currentValue}>{formatDisplayValue(shownValue)}</span>
              )}
            </div>

            {!isFullyFilled &&
              config.milestones.map((milestone) => {
                const position = (milestone.value / TARGET_VALUE) * 100;
                const isPassed = shownValue >= milestone.value;
                return (
                  <div key={milestone.value} className={styles.milestone} style={{ left: `${position}%` }}>
                    <div className={classNames(styles.milestoneIcon, { [styles.milestonePassed]: isPassed })}>
                      <img alt="" className={styles.treeImg} src={chaseBarTree} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className={styles.target} onClick={handleClick}>
          <img alt="" className={styles.targetTreeImg} src={chaseBarTree} />
          <span className={styles.targetValue}>{formatDisplayValue(TARGET_VALUE)}</span>
          <span className={styles.infoIcon}>{'i'}</span>
        </div>
      </div>
    </div>
  );
});

export default ChaseBar;
